import {
  AlertCircle,
  ArrowLeft,
  Briefcase,
  Calendar,
  Clock,
  Download,
  Loader2,
  User,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { LLMInputOutputViewer } from "../components/skill-check/LLMInputOutputViewer";
import { ScoringBreakdown } from "../components/skill-check/ScoringBreakdown";
import { Badge, getMatchLevelBadgeVariant, getStatusBadgeVariant } from "../components/ui/Badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { apiService } from "../services/api.service";
import type {
  ScoringBreakdown as ScoringBreakdownType,
  SkillCheckJobDetail,
  SkillCheckJobStepDetail,
} from "../types/admin.types";
import { downloadDetailedJSON } from "../utils/export";

type TabType = "overview" | "llm" | "scoring" | "error";

// 遅延ロード状態
type LazyLoadState<T> = {
  data: T | null;
  isLoading: boolean;
  isLoaded: boolean;
  error: string | null;
};

// ヘルパー関数：マッチレベルに応じたバーの色を取得
function getMatchLevelBarColor(level: number, matchLevel: number): string {
  if (level > matchLevel) {
    return "bg-gray-200";
  }
  if (level >= 4) {
    return "bg-green-500";
  }
  if (level >= 3) {
    return "bg-blue-500";
  }
  if (level >= 2) {
    return "bg-yellow-500";
  }
  return "bg-red-500";
}

// ヘルパー関数：判定結果に応じたバッジバリアントを取得
type BadgeVariant = "success" | "info" | "warning" | "error" | "default";
function getCriteriaResultBadgeVariant(result: string): BadgeVariant {
  const variantMap: Record<string, BadgeVariant> = {
    perfect: "success",
    good: "info",
    partial: "warning",
    mismatch: "error",
  };
  return variantMap[result] ?? "default";
}

// エラータブコンテンツ（遅延ロード対応）
type ErrorTabContentProps = {
  detail: SkillCheckJobDetail;
  stepsState: LazyLoadState<SkillCheckJobStepDetail[]>;
  onLoadSteps: () => void;
};

function ErrorTabContent({ detail, stepsState, onLoadSteps }: ErrorTabContentProps) {
  // エラータブ表示時にステップデータを自動ロード
  useEffect(() => {
    if (!(stepsState.isLoaded || stepsState.isLoading)) {
      onLoadSteps();
    }
  }, [stepsState.isLoaded, stepsState.isLoading, onLoadSteps]);

  const failedSteps = stepsState.data?.filter((s) => s.status === "failed") ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-red-600">
          <AlertCircle className="h-5 w-5" />
          エラー情報
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="font-medium text-gray-500 text-sm">エラーコード</p>
          <Badge className="mt-1" variant="error">
            {detail.errorCode ?? "UNKNOWN"}
          </Badge>
        </div>
        <div>
          <p className="font-medium text-gray-500 text-sm">エラーメッセージ</p>
          <p className="mt-1 rounded-md bg-red-50 p-3 font-mono text-gray-900 text-sm">
            {detail.errorMessage ?? "エラーメッセージなし"}
          </p>
        </div>
        {stepsState.isLoading && (
          <div className="flex items-center gap-2 text-gray-500 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" />
            失敗ステップを読み込み中...
          </div>
        )}
        {failedSteps.length > 0 && (
          <div>
            <p className="mb-2 font-medium text-gray-500 text-sm">失敗したステップ</p>
            {failedSteps.map((step) => (
              <div className="rounded-md bg-red-50 p-3" key={step.id}>
                <p className="font-medium text-red-800">
                  Step {step.stepNumber}: {step.stepName}
                </p>
                <p className="mt-1 text-red-600 text-sm">{step.errorMessage}</p>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function SkillCheckDetailPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const [detail, setDetail] = useState<SkillCheckJobDetail | null>(null);
  // 遅延ロード: LLMステップデータ（タブ選択時に取得）
  const [stepsState, setStepsState] = useState<LazyLoadState<SkillCheckJobStepDetail[]>>({
    data: null,
    isLoading: false,
    isLoaded: false,
    error: null,
  });
  // 遅延ロード: スコアリングデータ（タブ選択時に取得）
  const [scoringState, setScoringState] = useState<LazyLoadState<ScoringBreakdownType>>({
    data: null,
    isLoading: false,
    isLoaded: false,
    error: null,
  });
  const [activeTab, setActiveTab] = useState<TabType>("overview");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // エクスポート関連
  const [isExporting, setIsExporting] = useState(false);

  // 基本データの取得（初回ロード時）
  useEffect(() => {
    if (!jobId) {
      return;
    }

    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // 初回は詳細データのみ取得（PERF-001, PERF-003対応）
        const detailData = await apiService.getSkillCheckDetail(jobId);
        setDetail(detailData);
      } catch (err) {
        setError(err instanceof Error ? err.message : "データの取得に失敗しました");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [jobId]);

  // LLMステップデータの遅延ロード
  const loadStepsData = useCallback(async () => {
    if (!jobId || stepsState.isLoaded || stepsState.isLoading) {
      return;
    }

    setStepsState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const stepsData = await apiService.getSkillCheckSteps(jobId);
      setStepsState({
        data: stepsData,
        isLoading: false,
        isLoaded: true,
        error: null,
      });
    } catch (err) {
      setStepsState((prev) => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : "ステップデータの取得に失敗しました",
      }));
    }
  }, [jobId, stepsState.isLoaded, stepsState.isLoading]);

  // スコアリングデータの遅延ロード
  const loadScoringData = useCallback(async () => {
    if (!jobId || scoringState.isLoaded || scoringState.isLoading) {
      return;
    }

    if (detail?.status !== "COMPLETED") {
      setScoringState((prev) => ({
        ...prev,
        isLoaded: true,
        error: null,
      }));
      return;
    }

    setScoringState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const scoringData = await apiService.getScoringBreakdown(jobId);
      setScoringState({
        data: scoringData,
        isLoading: false,
        isLoaded: true,
        error: null,
      });
    } catch {
      // スコアリングデータがない場合は無視
      setScoringState((prev) => ({
        ...prev,
        isLoading: false,
        isLoaded: true,
        error: null,
      }));
    }
  }, [jobId, scoringState.isLoaded, scoringState.isLoading, detail?.status]);

  // タブ変更時に遅延ロードを実行
  useEffect(() => {
    if (activeTab === "llm") {
      loadStepsData();
    } else if (activeTab === "scoring") {
      loadScoringData();
    }
  }, [activeTab, loadStepsData, loadScoringData]);

  // 詳細データエクスポート
  const handleExportDetail = useCallback(async () => {
    if (!(detail && jobId)) {
      return;
    }

    try {
      setIsExporting(true);

      // ステップデータがまだ取得されていない場合は取得
      let steps = stepsState.data ?? [];
      if (!(stepsState.isLoaded || stepsState.isLoading)) {
        const stepsData = await apiService.getSkillCheckSteps(jobId);
        steps = stepsData;
      }

      // スコアリングデータがまだ取得されていない場合は取得（完了ジョブのみ）
      let scoring = scoringState.data;
      if (detail.status === "COMPLETED" && !scoringState.isLoaded && !scoringState.isLoading) {
        try {
          scoring = await apiService.getScoringBreakdown(jobId);
        } catch {
          // スコアリングデータがない場合は無視
          scoring = null;
        }
      }

      // JSONファイルをダウンロード
      downloadDetailedJSON(detail, steps, scoring);
    } finally {
      setIsExporting(false);
    }
  }, [
    detail,
    jobId,
    stepsState.data,
    stepsState.isLoaded,
    stepsState.isLoading,
    scoringState.data,
    scoringState.isLoaded,
    scoringState.isLoading,
  ]);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="space-y-4">
        <Link
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
          to="/admin/skill-checks"
        >
          <ArrowLeft className="h-4 w-4" />
          一覧に戻る
        </Link>
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
          {error ?? "データが見つかりません"}
        </div>
      </div>
    );
  }

  // タブ表示条件（遅延ロードのため、タブは常に表示し中身でローディングを表示）
  const tabs: { id: TabType; label: string; show: boolean }[] = [
    { id: "overview", label: "概要", show: true },
    // LLMタブは常に表示（遅延ロードのため）
    { id: "llm", label: "LLM入出力", show: true },
    // スコアリングタブは完了ジョブのみ表示
    { id: "scoring", label: "スコアリング内訳", show: detail.status === "COMPLETED" },
    { id: "error", label: "エラー情報", show: detail.status === "FAILED" },
  ];

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            className="rounded-md p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900"
            to="/admin/skill-checks"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="font-bold text-2xl text-gray-900">スキルチェック詳細</h1>
            <p className="text-gray-500 text-sm">ID: {detail.id}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-gray-700 text-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={isExporting}
            onClick={handleExportDetail}
            title="詳細データをJSONでエクスポート（LLM入出力含む）"
            type="button"
          >
            {isExporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            {isExporting ? "エクスポート中..." : "詳細エクスポート"}
          </button>
          <Badge className="px-3 py-1 text-sm" variant={getStatusBadgeVariant(detail.status)}>
            {detail.status}
          </Badge>
        </div>
      </div>

      {/* 基本情報カード */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="flex items-center gap-4 py-6">
            <div className="rounded-full bg-blue-100 p-3">
              <User className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-gray-500 text-sm">候補者</p>
              <p className="font-semibold">{detail.candidateName ?? "名前未取得"}</p>
              <p className="text-gray-500 text-sm">{detail.candidateSource ?? "-"}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-4 py-6">
            <div className="rounded-full bg-green-100 p-3">
              <Briefcase className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-gray-500 text-sm">求人</p>
              <p className="max-w-48 truncate font-semibold" title={detail.jobPostingTitle}>
                {detail.jobPostingTitle}
              </p>
              <p className="text-gray-500 text-sm">{detail.companyName}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-4 py-6">
            <div className="rounded-full bg-amber-100 p-3">
              <Calendar className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <p className="text-gray-500 text-sm">作成日時</p>
              <p className="font-semibold">
                {new Date(detail.createdAt).toLocaleDateString("ja-JP", {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                })}
              </p>
              <p className="text-gray-500 text-sm">
                {new Date(detail.createdAt).toLocaleTimeString("ja-JP", {
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                })}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-4 py-6">
            <div className="rounded-full bg-purple-100 p-3">
              <Clock className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-gray-500 text-sm">処理時間</p>
              {detail.completedAt && detail.startedAt ? (
                <p className="font-semibold">
                  {(
                    (new Date(detail.completedAt).getTime() -
                      new Date(detail.startedAt).getTime()) /
                    1000
                  ).toFixed(1)}
                  秒
                </p>
              ) : (
                <p className="font-semibold">-</p>
              )}
              <p className="text-gray-500 text-sm">リトライ: {detail.retryCount}回</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* マッチレベル */}
      {detail.overallMatchLevel !== null && (
        <Card>
          <CardContent className="py-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">総合マッチレベル</p>
                <div className="mt-2 flex items-center gap-4">
                  <Badge
                    className="px-4 py-2 text-lg"
                    variant={getMatchLevelBadgeVariant(detail.overallMatchLevel)}
                  >
                    Lv.{detail.overallMatchLevel}
                  </Badge>
                  <div className="flex gap-1">
                    {[0, 1, 2, 3, 4, 5].map((level) => (
                      <div
                        className={`h-2 w-8 rounded ${getMatchLevelBarColor(level, detail.overallMatchLevel!)}`}
                        key={level}
                      />
                    ))}
                  </div>
                </div>
              </div>
              {detail.evaluationComment && (
                <div className="max-w-md">
                  <p className="mb-1 text-gray-500 text-sm">評価コメント</p>
                  <p className="text-gray-700 text-sm">{detail.evaluationComment}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* タブ */}
      <div className="border-gray-200 border-b">
        <nav className="flex gap-4">
          {tabs
            .filter((tab) => tab.show)
            .map((tab) => (
              <button
                className={`border-b-2 px-1 py-3 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? "border-blue-600 text-blue-600"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                }
                `}
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                type="button"
              >
                {tab.label}
              </button>
            ))}
        </nav>
      </div>

      {/* タブコンテンツ */}
      {activeTab === "overview" && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* スキル情報 */}
          <Card>
            <CardHeader>
              <CardTitle>マッチしたスキル</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="mb-2 font-medium text-gray-500 text-sm">必須スキル</p>
                <div className="flex flex-wrap gap-2">
                  {detail.matchedRequiredSkills.length > 0 ? (
                    detail.matchedRequiredSkills.map((skill) => (
                      <Badge key={skill} variant="success">
                        {skill}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-gray-400 text-sm">なし</span>
                  )}
                </div>
              </div>
              <div>
                <p className="mb-2 font-medium text-gray-500 text-sm">歓迎スキル</p>
                <div className="flex flex-wrap gap-2">
                  {detail.matchedPreferredSkills.length > 0 ? (
                    detail.matchedPreferredSkills.map((skill) => (
                      <Badge key={skill} variant="info">
                        {skill}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-gray-400 text-sm">なし</span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>不足スキル</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="mb-2 font-medium text-gray-500 text-sm">必須スキル</p>
                <div className="flex flex-wrap gap-2">
                  {detail.missingRequiredSkills.length > 0 ? (
                    detail.missingRequiredSkills.map((skill) => (
                      <Badge key={skill} variant="error">
                        {skill}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-gray-400 text-sm">なし</span>
                  )}
                </div>
              </div>
              <div>
                <p className="mb-2 font-medium text-gray-500 text-sm">歓迎スキル</p>
                <div className="flex flex-wrap gap-2">
                  {detail.missingPreferredSkills.length > 0 ? (
                    detail.missingPreferredSkills.map((skill) => (
                      <Badge key={skill} variant="warning">
                        {skill}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-gray-400 text-sm">なし</span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 部分マッチスキル */}
          {detail.partialMatchSkills.length > 0 && (
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>部分マッチスキル</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {detail.partialMatchSkills.map((skill, index) => (
                    <div
                      className="flex items-start gap-4 rounded-lg bg-yellow-50 p-3"
                      key={`${skill.skill}-${index}`}
                    >
                      <Badge variant="warning">{skill.skill}</Badge>
                      <div className="flex-1">
                        <p className="text-gray-700 text-sm">{skill.comment}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* カスタム判定結果 */}
          {detail.criteriaResults.length > 0 && (
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>カスタム判定結果</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {detail.criteriaResults.map((result) => (
                    <div
                      className="flex items-start gap-4 rounded-lg bg-gray-50 p-3"
                      key={result.id}
                    >
                      <Badge variant={getCriteriaResultBadgeVariant(result.result)}>
                        {result.result}
                      </Badge>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{result.criteriaText}</p>
                        <p className="mt-1 text-gray-500 text-sm">{result.reason}</p>
                      </div>
                      <Badge className="text-xs" variant="default">
                        {result.importance}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {activeTab === "llm" && (
        <>
          {stepsState.isLoading && (
            <Card>
              <CardContent className="flex items-center justify-center py-12">
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                  <p className="text-gray-500 text-sm">LLM入出力データを読み込み中...</p>
                </div>
              </CardContent>
            </Card>
          )}
          {stepsState.error && (
            <Card>
              <CardContent className="py-8">
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
                  {stepsState.error}
                </div>
              </CardContent>
            </Card>
          )}
          {stepsState.isLoaded && stepsState.data && stepsState.data.length > 0 && (
            <LLMInputOutputViewer steps={stepsState.data} />
          )}
          {stepsState.isLoaded &&
            (!stepsState.data || stepsState.data.length === 0) &&
            !stepsState.error && (
              <Card>
                <CardContent className="py-8 text-center text-gray-500">
                  ステップデータがありません
                </CardContent>
              </Card>
            )}
        </>
      )}

      {activeTab === "scoring" && (
        <>
          {scoringState.isLoading && (
            <Card>
              <CardContent className="flex items-center justify-center py-12">
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                  <p className="text-gray-500 text-sm">スコアリング内訳を読み込み中...</p>
                </div>
              </CardContent>
            </Card>
          )}
          {scoringState.isLoaded && scoringState.data && (
            <ScoringBreakdown data={scoringState.data} />
          )}
          {scoringState.isLoaded && !scoringState.data && (
            <Card>
              <CardContent className="py-8 text-center text-gray-500">
                スコアリングデータがありません
              </CardContent>
            </Card>
          )}
        </>
      )}

      {activeTab === "error" && detail.status === "FAILED" && (
        <ErrorTabContent detail={detail} onLoadSteps={loadStepsData} stepsState={stepsState} />
      )}
    </div>
  );
}
